import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { RequestError } from '../../../interfaces/error.interface';
import { createEntityAdapter } from '../../../helpers/create-entity-adapter';
import { GetArticleParams, GetArticlesResponse } from '../interfaces/get-articles';
import { Article } from '../../../interfaces/article.interface';
import { ClassifiedArticle, GetClassifiedArticlesResponse } from '../interfaces/get-classified-articles';

export interface ArticleState {
  articles: {
    ids: string[];
    entities: Record<string, Article>;
  };
  classifiedArticles: {
    ids: string[];
    entities: Record<string, ClassifiedArticle>;
  };
  article: Article | null;
  options: GetArticleParams;
  page: number;
  size: number;
  total: number;
  noMore: boolean;
  errorCode: number | null;
}

const initialState: ArticleState = {
  articles: {
    ids: [],
    entities: {},
  },
  classifiedArticles: {
    ids: [],
    entities: {},
  },
  article: null,
  options: {},
  page: 1,
  size: 100,
  total: 0,
  noMore: false,
  errorCode: null,
};

const articleAdapter = createEntityAdapter<Article>((article: Article) => article.id);
const classifiedArticleAdapter = createEntityAdapter<ClassifiedArticle>((article: ClassifiedArticle) => article.code);

const articleSlice = createSlice({
  name: 'article',
  initialState,
  reducers: {
    getArticlesInitApiAction: (state) => {
      state.errorCode = null;
    },
    getArticlesSuccessApiAction: (
      state,
      action: PayloadAction<GetArticlesResponse>
    ) => {
      articleAdapter.addMany(action.payload.products, state.articles);
      state.total = action.payload.pagination.total_elements;
      state.page = action.payload.pagination.page;
      state.size = action.payload.pagination.size;
      state.noMore = action.payload.products.length < state.size;
    },
    getArticlesFailureApiAction: (state, action: PayloadAction<RequestError>) => {
      articleAdapter.removeAll(state.articles);
      state.errorCode = action.payload.errorCode;
    },
    getClassifiedArticlesInitApiAction: (state) => {
      state.errorCode = null;
    },
    getClassifiedArticlesSuccessApiAction: (
      state,
      action: PayloadAction<GetClassifiedArticlesResponse>
    ) => {
      classifiedArticleAdapter.addMany(action.payload.lightweight_product_storages, state.classifiedArticles);
      state.total = action.payload.pagination.total_elements;
      state.page = action.payload.pagination.page;
      state.size = action.payload.pagination.size;
      state.noMore = action.payload.lightweight_product_storages.length < state.size;
    },
    getClassifiedArticlesFailureApiAction: (state, action: PayloadAction<RequestError>) => {
      articleAdapter.removeAll(state.articles);
      state.errorCode = action.payload.errorCode;
    },
    postArticleInitApiAction: (state) => {
      state.errorCode = null;
    },
    postArticleSuccessApiAction: () => {},
    postArticleFailuireApiAction: (state, action: PayloadAction<RequestError>) => {
      state.errorCode = action.payload.errorCode;
    },
    postArticleFileInitApiAction: (state) => {
      state.errorCode = null;
    },
    postArticleFileSuccessApiAction: () => {},
    postArticleFileFailuireApiAction: (state, action: PayloadAction<RequestError>) => {
      state.errorCode = action.payload.errorCode;
    },
    putArticleInitApiAction: (state) => {
      state.errorCode = null;
    },
    putArticleSuccessApiAction: () => {},
    putArticleFailuireApiAction: (state, action: PayloadAction<RequestError>) => {
      state.errorCode = action.payload.errorCode;
    },
    deleteArticleInitApiAction: () => {},
    deleteArticleSuccessApiAction: () => {},
    deleteArticleFailuireApiAction: () => {},
    deleteFileInitApiAction: () => {},
    deleteFileSuccessApiAction: () => {},
    deleteFileFailuireApiAction: () => {},
    setArticleAction: (state, action: PayloadAction<Article>) => {
      state.article = action.payload;
    },
    setOptionsAction: (state, action: PayloadAction<GetArticleParams>) => {
      state.options = action.payload;
      articleAdapter.removeAll(state.articles);
      state.page = 1;
    },
    resetOptionsAction: (state) => {
      state.options = {};
    },
    increasePageAction: (state) => {
      state.page += 1;
    },
    resetPageAction: (state) => {
      state.page = 1;
    },
    articlesClearAction: (state) => {
      classifiedArticleAdapter.removeAll(state.classifiedArticles)
    },
    articleClearAction: (state) => {
      state.article = null;
      state.errorCode = null;
    },
  },
});

export const {
  getArticlesInitApiAction,
  getArticlesSuccessApiAction,
  getArticlesFailureApiAction,
  getClassifiedArticlesInitApiAction,
  getClassifiedArticlesSuccessApiAction,
  getClassifiedArticlesFailureApiAction,
  postArticleInitApiAction,
  postArticleSuccessApiAction,
  postArticleFailuireApiAction,
  putArticleInitApiAction,
  putArticleSuccessApiAction,
  putArticleFailuireApiAction,
  deleteArticleInitApiAction,
  deleteArticleSuccessApiAction,
  deleteArticleFailuireApiAction,
  postArticleFileInitApiAction,
  postArticleFileSuccessApiAction,
  postArticleFileFailuireApiAction,
  deleteFileInitApiAction,
  deleteFileSuccessApiAction,
  deleteFileFailuireApiAction,
  setArticleAction,
  increasePageAction,
  resetPageAction,
  setOptionsAction,
  resetOptionsAction,
  articleClearAction,
  articlesClearAction,
} = articleSlice.actions;

export default articleSlice.reducer;
